/**
 * REP-002 — a small self-contained line chart.
 *
 * No charting library: the app has zero UI-kit/chart dependencies by design
 * (see the design-token CSS system and the hand-rolled DataTable), and a
 * handful of SVG polylines needs neither the bundle weight nor the API
 * surface of a real charting package.
 *
 * Time always flows left-to-right here, the same convention the app already
 * uses for numbers and dates in an RTL document (`.ltr-nums`) — a chart
 * mirrored per-locale would make "growing over time" visually ambiguous.
 */
export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export function MiniChart({
  categories,
  series,
  height = 160,
  ariaLabel,
}: {
  categories: string[];
  series: ChartSeries[];
  height?: number;
  ariaLabel: string;
}) {
  const width = 600;
  const paddingLeft = 34;
  const paddingBottom = 20;
  const paddingTop = 10;
  const plotWidth = width - paddingLeft - 8;
  const plotHeight = height - paddingBottom - paddingTop;

  const maxValue = Math.max(1, ...series.flatMap((s) => s.values));
  const stepX = categories.length > 1 ? plotWidth / (categories.length - 1) : 0;

  const pointsFor = (values: number[]) =>
    values
      .map((value, index) => {
        const x = paddingLeft + index * stepX;
        const y = paddingTop + plotHeight - (value / maxValue) * plotHeight;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  const yTicks = [0, 0.5, 1].map((fraction) => Math.round(maxValue * fraction));
  const hasData = series.some((s) => s.values.some((v) => v > 0));

  // Sparse x-axis labels: first, middle, last — dense category lists (30 days,
  // 24 hours) would otherwise overlap into unreadable text.
  const labelIndexes = new Set(
    [0, Math.floor((categories.length - 1) / 2), categories.length - 1].filter(
      (i) => i >= 0 && i < categories.length,
    ),
  );

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        style={{ width: '100%', height: 'auto', direction: 'ltr' }}
      >
        {yTicks.map((tick, index) => {
          const y = paddingTop + plotHeight - (tick / maxValue) * plotHeight;
          return (
            <g key={index}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - 8}
                y2={y}
                stroke="hsl(var(--border))"
                strokeWidth={1}
              />
              <text x={0} y={y + 4} fontSize={9} fill="hsl(var(--muted-foreground))">
                {tick}
              </text>
            </g>
          );
        })}

        {categories.map((label, index) =>
          labelIndexes.has(index) ? (
            <text
              key={label}
              x={paddingLeft + index * stepX}
              y={height - 4}
              fontSize={9}
              fill="hsl(var(--muted-foreground))"
              textAnchor={index === 0 ? 'start' : index === categories.length - 1 ? 'end' : 'middle'}
            >
              {label}
            </text>
          ) : null,
        )}

        {hasData &&
          series.map((s) => (
            <polyline
              key={s.key}
              points={pointsFor(s.values)}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
      </svg>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span aria-hidden className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: s.color }} />
            <span className="text-[11px] text-muted-foreground">{s.label}</span>
          </span>
        ))}
      </div>

      {/* Accessible fallback: the same series as a data table, visually hidden. */}
      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th>{ariaLabel}</th>
            {categories.map((label) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map((s) => (
            <tr key={s.key}>
              <th>{s.label}</th>
              {s.values.map((value, index) => (
                <td key={index}>{value}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
